import React, { useState, useEffect } from 'react';
import Standings from './Standings';
import Matches from './Matches';
import SeasonSelector from './SeasonSelector';
import Teams from './Teams';
import TeamDetail from './TeamDetail';
import Rules from './Rules';
import Login from './Login';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import logo from './assets/Posázavská_liga_starých_gard.png';

function Admin({ seasonId }) {
  // Moderní Bootstrap stylování celé Admin sekce
  // ... (zbytek kódu bude přeformátován níže)

  const [teams, setTeams] = useState([]);
  const [newTeam, setNewTeam] = useState("");
  const [error, setError] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null); // tým k editaci
  const [teamForm, setTeamForm] = useState({ name: '', logo: null, logoPreview: '' });
  const [teamError, setTeamError] = useState(null);
  // Kontakty
  const [contacts, setContacts] = useState([]);
  const [contactForm, setContactForm] = useState({ name: '', phone: '', email: '' });
  const [editingContact, setEditingContact] = useState(null);
  const [contactError, setContactError] = useState(null);


  // --- Zápasy ---
  const [matches, setMatches] = useState([]);
  const [matchFilterTeam, setMatchFilterTeam] = useState(''); // id týmu pro filtraci zápasů
  const [editingMatch, setEditingMatch] = useState(null); // zápas k editaci nebo null
  const [matchForm, setMatchForm] = useState({
    home_team: '',
    away_team: '',
    date: '',
    time: '',
    home_score: '',
    away_score: '',
    home_halftime: '',
    away_halftime: ''
  });

  // Načíst týmy a zápasy
  useEffect(() => {
    if (!seasonId) return;
    fetch(`/api/teams?season_id=${seasonId}`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setTeams(data);
          setError(null);
        } else if (data && data.error) {
          setError(data.error);
          setTeams([]);
        }
      })
      .catch(e => setError('Chyba při načítání týmů.'));
    fetch(`/api/matches?season_id=${seasonId}`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setMatches(data);
        else setMatches([]);
      });
  }, [seasonId]);

  // Přidání týmu
  const handleAddTeam = (e) => {
    e.preventDefault();
    fetch('/api/teams', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newTeam, season_id: seasonId })
    })
      .then(res => res.json())
      .then(team => {
        setTeams(Array.isArray(teams) ? [...teams, team] : [team]);
        setNewTeam("");
      });
  };

  // Editace týmu (otevře formulář)
  const handleEditTeam = (team) => {
    setEditingTeam(team);
    setTeamForm({ name: team.name, logo: null, logoPreview: team.logo ? `/logos/${team.logo}` : '' });
    setTeamError(null);
    // Načíst kontakty
    fetch(`/api/teams/${team.id}/contacts`)
      .then(res => res.json())
      .then(data => setContacts(Array.isArray(data) ? data : []));
    setContactForm({ name: '', phone: '', email: '' });
    setEditingContact(null);
    setContactError(null);
  };

  // Přidání/úprava kontaktu
  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name) return setContactError('Jméno je povinné.');
    setContactError(null);
    if (editingContact) {
      // update
      const res = await fetch(`/api/contacts/${editingContact.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      if (!res.ok) return setContactError('Chyba při ukládání kontaktu.');
    } else {
      // create
      const res = await fetch(`/api/teams/${editingTeam.id}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      if (!res.ok) return setContactError('Chyba při ukládání kontaktu.');
    }
    // refresh
    fetch(`/api/teams/${editingTeam.id}/contacts`)
      .then(res => res.json())
      .then(data => setContacts(Array.isArray(data) ? data : []));
    setContactForm({ name: '', phone: '', email: '' });
    setEditingContact(null);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({ name: contact.name, phone: contact.phone || '', email: contact.email || '' });
    setContactError(null);
  };

  const handleDeleteContact = (contact) => {
    if (!window.confirm('Opravdu smazat kontakt?')) return;
    fetch(`/api/contacts/${contact.id}`, { method: 'DELETE' })
      .then(res => res.json())
      .then(() => {
        setContacts(contacts.filter(c => c.id !== contact.id));
      });
  };


  // Zrušit editaci týmu
  const handleCancelEditTeam = () => {
    setEditingTeam(null);
    setTeamForm({ name: '', logo: null, logoPreview: '' });
    setTeamError(null);
  };

  // Odeslání úpravy týmu
  const handleTeamFormSubmit = async (e) => {
    e.preventDefault();
    setTeamError(null);
    // Nejprve PATCH/PUT na jméno
    const res = await fetch(`/api/teams/${editingTeam.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: teamForm.name, logo: editingTeam.logo })
    });
    if (!res.ok) {
      setTeamError('Chyba při ukládání týmu.');
      return;
    }
    // Pokud je vybrán nový obrázek, uploadni ho
    let logoUrl = editingTeam.logo;
    if (teamForm.logo) {
      const formData = new FormData();
      formData.append('logo', teamForm.logo);
      const resLogo = await fetch(`/api/teams/${editingTeam.id}/logo`, {
        method: 'POST',
        body: formData
      });
      if (resLogo.ok) {
        const data = await resLogo.json();
        logoUrl = data.logo;
      } else {
        setTeamError('Chyba při nahrávání loga.');
        return;
      }
    }
    // Aktualizuj logo v DB pokud bylo změněno
    if (logoUrl !== editingTeam.logo) {
      await fetch(`/api/teams/${editingTeam.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: teamForm.name, logo: logoUrl })
      });
    }
    // Refresh týmů
    fetch(`/api/teams?season_id=${seasonId}`)
      .then(res => res.json())
      .then(data => setTeams(Array.isArray(data) ? data : []));
    setEditingTeam(null);
    setTeamForm({ name: '', logo: null, logoPreview: '' });
  };

  // Výběr souboru loga
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setTeamForm(f => ({ ...f, logo: file }));
    const reader = new FileReader();
    reader.onload = ev => setTeamForm(f => ({ ...f, logoPreview: ev.target.result }));
    reader.readAsDataURL(file);
  };

  // Smazání týmu
  const handleDeleteTeam = (team) => {
    if (!window.confirm('Opravdu smazat tým?')) return;
    fetch(`/api/teams/${team.id}?season_id=${seasonId}`, { method: 'DELETE' })
      .then(res => res.json())
      .then(result => {
        if (result && result.error) {
          setTeamError(result.error);
        } else {
          setTeams(teams.filter(t => t.id !== team.id));
        }
      });
  };


  // Přidání/úprava zápasu
  const handleMatchSubmit = (e) => {
    e.preventDefault();
    if (!matchForm.home_team || !matchForm.away_team || matchForm.home_team === matchForm.away_team) return;
    const payload = {
      ...matchForm,
      home_team: Number(matchForm.home_team),
      away_team: Number(matchForm.away_team),
      date: matchForm.date,
      time: matchForm.time,
      home_score: matchForm.home_score === '' ? null : Number(matchForm.home_score),
      away_score: matchForm.away_score === '' ? null : Number(matchForm.away_score),
      home_halftime: matchForm.home_halftime === '' ? null : Number(matchForm.home_halftime),
      away_halftime: matchForm.away_halftime === '' ? null : Number(matchForm.away_halftime),
      season_id: seasonId
    };
    if (editingMatch) {
      fetch(`/api/matches/${editingMatch.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(res => res.json())
        .then(m => {
          // Po úpravě zápasu načti znovu zápasy z API (aby se propsaly i případné změny z backendu)
          fetch(`/api/matches?season_id=${seasonId}`)
            .then(res => res.json())
            .then(data => {
              setMatches(Array.isArray(data) ? data : []);
              setEditingMatch(null);
              setMatchForm({ home_team: '', away_team: '', date: '', time: '', home_score: '', away_score: '', home_halftime: '', away_halftime: '' });
            });
        });
    } else {
      fetch('/api/matches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(res => res.json())
        .then(m => {
          setMatches([...matches, m]);
          setMatchForm({ home_team: '', away_team: '', date: '', time: '', home_score: '', away_score: '', home_halftime: '', away_halftime: '' });
        });
    }
  };

  // Editace zápasu
  const handleEditMatch = (match) => {
    setEditingMatch(match);
    setMatchForm({
      home_team: match.home_team,
      away_team: match.away_team,
      date: match.date,
      time: match.time ?? '',
      home_score: match.home_score ?? '',
      away_score: match.away_score ?? '',
      home_halftime: match.home_halftime ?? '',
      away_halftime: match.away_halftime ?? ''
    });
  };

  // Smazání zápasu
  const handleDeleteMatch = (id) => {
    if (!window.confirm('Opravdu smazat tento zápas?')) return;
    fetch(`/api/matches/${id}`, { method: 'DELETE' })
      .then(res => res.json())
      .then(() => setMatches(matches.filter(m => m.id !== id)));
  };

  if (error) return <div style={{color:'red'}}>{error}</div>;
  if (!teams || !Array.isArray(teams)) return null;

  // Seřazení zápasů podle data a času
  const sortedMatches = [...matches].sort((a, b) => {
    const dateA = new Date(a.date + (a.time ? 'T' + a.time : 'T00:00'));
    const dateB = new Date(b.date + (b.time ? 'T' + b.time : 'T00:00'));
    return dateA - dateB;
  });

  // Filtrování zápasů podle týmu
  const filteredMatches = matchFilterTeam
    ? sortedMatches.filter(m => String(m.home_team) === matchFilterTeam || String(m.away_team) === matchFilterTeam)
    : sortedMatches;

  return (
    <div className="container py-4">
      <div className="row">
        {/* Správa týmů */}
        <div className="col-12 mb-4">
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h2 className="card-title mb-4">Správa týmů</h2>
              <form className="row g-2 align-items-end mb-3" onSubmit={handleAddTeam}>
                <div className="col">
                  <label className="form-label">Název týmu</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newTeam}
                    onChange={e => setNewTeam(e.target.value)}
                    placeholder="Název týmu"
                    required
                  />
                </div>
                <div className="col-auto">
                  <button type="submit" className="btn btn-success">Přidat tým</button>
                </div>
              </form>
              {editingTeam && (
                <div>
                  <form className="border rounded p-3 mb-3 bg-light" onSubmit={handleTeamFormSubmit}>
                    <h4 className="mb-3">Upravit tým</h4>
                    <div className="mb-3">
                      <label className="form-label">Název týmu</label>
                      <input
                        type="text"
                        className="form-control"
                        value={teamForm.name}
                        onChange={e => setTeamForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="Název týmu"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Logo</label>
                      <input type="file" accept="image/*" className="form-control" onChange={handleLogoChange} />
                      {teamForm.logoPreview && <img src={teamForm.logoPreview} alt="logo náhled" className="ms-3 border bg-white rounded" style={{height:40, verticalAlign:'middle'}} />}
                    </div>
                    <div className="mb-3">
                      <button type="submit" className="btn btn-primary me-2">Uložit</button>
                      <button type="button" className="btn btn-outline-secondary" onClick={handleCancelEditTeam}>Zrušit</button>
                    </div>
                    {teamError && <div className="alert alert-danger py-2 mb-3">{teamError}</div>}
                  </form>
                  <div className="bg-white rounded p-3 mt-4">
                    <h5>Kontaktní osoby</h5>
                    {contacts.length === 0 && <div className="text-muted">Žádné kontaktní osoby.</div>}
                    <ul className="list-group mb-3">
                      {contacts.map(contact => (
                        <li key={contact.id} className="list-group-item d-flex align-items-center justify-content-between">
                          <span>
                            <b>{contact.name}</b> {contact.phone && (<span className="ms-2">{contact.phone}</span>)} {contact.email && (<span className="ms-2">{contact.email}</span>)}
                          </span>
                          <span>
                            <button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={()=>handleEditContact(contact)}>Upravit</button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={()=>handleDeleteContact(contact)}>Smazat</button>
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className="row g-2 align-items-end" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleContactFormSubmit(e); }}}>
                      <div className="col">
                        <input type="text" className="form-control" value={contactForm.name} onChange={e=>setContactForm(f=>({...f,name:e.target.value}))} placeholder="Jméno" required />
                      </div>
                      <div className="col">
                        <input type="text" className="form-control" value={contactForm.phone} onChange={e=>setContactForm(f=>({...f,phone:e.target.value}))} placeholder="Telefon" />
                      </div>
                      <div className="col">
                        <input type="email" className="form-control" value={contactForm.email} onChange={e=>setContactForm(f=>({...f,email:e.target.value}))} placeholder="Email" />
                      </div>
                      <div className="col-auto">
                        <button type="button" className={`btn ${editingContact ? 'btn-primary' : 'btn-success'}`} onClick={handleContactFormSubmit}>{editingContact ? 'Uložit kontakt' : 'Přidat kontakt'}</button>
                      </div>
                      {editingContact && (
                        <div className="col-auto">
                          <button type="button" className="btn btn-outline-secondary" onClick={()=>{setEditingContact(null);setContactForm({name:'',phone:'',email:''});}}>Zrušit</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <ul className="list-group">
                {teams.length === 0 && <li className="list-group-item text-muted">V této sezóně zatím nejsou žádné týmy.</li>}
                {teams.map(team => (
                  <li key={team.id} className="list-group-item d-flex align-items-center justify-content-between">
                    <span className="d-flex align-items-center gap-2">
                      {team.logo && <img src={`/logos/${team.logo.replace(/^logos[\\/]/, '')}`} alt="logo" className="border bg-white rounded me-2" style={{height:32}} />}
                      {team.name}
                    </span>
                    <span>
                      <button className="btn btn-sm btn-outline-primary me-2" onClick={()=>handleEditTeam(team)}>Upravit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={()=>handleDeleteTeam(team)}>Smazat</button>
                    </span>
                  </li>
                ))}
              </ul>
              {teamError && !editingTeam && <div className="alert alert-danger py-2 mt-3 mb-0">{teamError}</div>}
            </div>
          </div>
        </div>
        {/* Správa zápasů */}
        <div className="col-12">
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h2 className="card-title mb-4">Správa zápasů</h2>
              <div className="mb-3">
                <label className="form-label me-2">Filtrovat podle týmu:</label>
                <select
                  className="form-select d-inline-block w-auto"
                  value={matchFilterTeam}
                  onChange={e => setMatchFilterTeam(e.target.value)}
                >
                  <option value=''>Všechny týmy</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <form className="row g-2 align-items-end mb-3" onSubmit={handleMatchSubmit}>
                <div className="col-12 col-md-5">
                  <select className="form-select" value={matchForm.home_team} onChange={e => setMatchForm(f => ({...f, home_team: e.target.value}))} required>
                    <option value=''>Domácí tým</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div className="col-auto d-flex align-items-center">vs</div>
                <div className="col-12 col-md-5">
                  <select className="form-select" value={matchForm.away_team} onChange={e => setMatchForm(f => ({...f, away_team: e.target.value}))} required>
                    <option value=''>Hostující tým</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div className="col-6 col-md-4">
                  <input type="date" className="form-control" value={matchForm.date} onChange={e => setMatchForm(f => ({...f, date: e.target.value}))} required />
                </div>
                <div className="col-6 col-md-3">
                  <input type="time" className="form-control" value={matchForm.time} onChange={e => setMatchForm(f => ({...f, time: e.target.value}))} required />
                </div>
                <div className="col-6 col-md-2">
                  <input type="number" className="form-control" value={matchForm.home_score} onChange={e => setMatchForm(f => ({...f, home_score: e.target.value}))} placeholder="Domácí skóre" min={0} />
                </div>
                <div className="col-auto d-flex align-items-center">:</div>
                <div className="col-6 col-md-2">
                  <input type="number" className="form-control" value={matchForm.away_score} onChange={e => setMatchForm(f => ({...f, away_score: e.target.value}))} placeholder="Hosté skóre" min={0} />
                </div>
                <div className="col-6 col-md-2">
                  <input type="number" className="form-control" value={matchForm.home_halftime} onChange={e => setMatchForm(f => ({...f, home_halftime: e.target.value}))} placeholder="Poločas domácí" min={0} />
                </div>
                <div className="col-auto d-flex align-items-center">:</div>
                <div className="col-6 col-md-2">
                  <input type="number" className="form-control" value={matchForm.away_halftime} onChange={e => setMatchForm(f => ({...f, away_halftime: e.target.value}))} placeholder="Poločas hosté" min={0} />
                </div>
                <div className="col-12 col-md-auto">
                  <button type="submit" className="btn btn-success me-2">{editingMatch ? 'Uložit změny' : 'Přidat zápas'}</button>
                  {editingMatch && <button type="button" className="btn btn-outline-secondary" onClick={()=>{setEditingMatch(null);setMatchForm({home_team:'',away_team:'',date:'',time:'',home_score:'',away_score:'',home_halftime:'',away_halftime:''});}}>Zrušit</button>}
                </div>
              </form>
              <div className="table-responsive">
                <table className="table table-striped table-hover table-bordered bg-white">
                  <thead className="table-light">
                    <tr>
                      <th className="text-center">Datum</th>
                      <th className="text-center">Čas</th>
                      <th>Domácí</th>
                      <th className="text-center">Výsledek</th>
                      <th>Hosté</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {matches.length === 0 ? (
                      <tr><td colSpan={6}>V této sezóně zatím nejsou žádné zápasy.</td></tr>
                    ) : filteredMatches.map(match => (
                      <tr key={match.id}>
                        <td className="text-center">{match.date ? new Date(match.date).toLocaleDateString('cs-CZ') : '-'}</td>
                        <td className="text-center">{match.time || '-'}</td>
                        <td>{teams.find(t=>t.id===match.home_team)?.name || '-'}</td>
                        <td className="text-center">{match.home_score != null && match.away_score != null ? `${match.home_score} : ${match.away_score}` : '-'}</td>
                        <td>{teams.find(t=>t.id===match.away_team)?.name || '-'}</td>
                        <td>
                          <button className="btn btn-sm btn-outline-primary me-2" onClick={()=>handleEditMatch(match)}>Upravit</button>
                          <button className="btn btn-sm btn-outline-danger" onClick={()=>handleDeleteMatch(match.id)}>Smazat</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// Komponenta pro responzivní zobrazení výběru sezóny
function ResponsiveSeasonSelector({ seasonId, setSeasonId, admin }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Na desktopu vždy zobrazit, na mobilu skrýt při změně velikosti
      if (!mobile) setIsOpen(true);
      else setIsOpen(false);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Zavolat na začátku

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ position: 'absolute', top: 50, left: 24, zIndex: 10 }}>
      {isMobile && (
        <button onClick={() => setIsOpen(!isOpen)} className="btn btn-light shadow-sm mb-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-calendar-event" viewBox="0 0 16 16">
            <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5z"/>
            <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4z"/>
          </svg> Sezóna
        </button>
      )}
      <div style={{ display: !isMobile || isOpen ? 'block' : 'none' }}>
        <SeasonSelector seasonId={seasonId} setSeasonId={setSeasonId} admin={admin} />
      </div>
    </div>
  );
}

function App() {
  const [isAdmin, setIsAdmin] = React.useState(localStorage.getItem('isAdmin') === 'true');

  const handleLogin = () => setIsAdmin(true);
  const handleLogout = () => {
    localStorage.removeItem('isAdmin');
    setIsAdmin(false);
  };

  const [seasonId, setSeasonId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Pomocný hook na zvýraznění aktivního tlačítka
  function useActivePath() {
    if (location.pathname.startsWith('/matches')) return 'matches';
    if (location.pathname.startsWith('/teams/') && location.pathname !== '/teams') return '';
    if (location.pathname.startsWith('/teams')) return 'teams';
    if (location.pathname.startsWith('/admin')) return 'admin';
    if (location.pathname.startsWith('/pravidla')) return 'pravidla';
    return 'standings';
  }
  const active = useActivePath();

  // Pokud se změní sezóna a jsme na detailu týmu, přesměruj na úvodní stránku
  useEffect(() => {
    if (location.pathname.match(/^\/teams\/[0-9]+/)) {
      navigate('/');
    }
    // eslint-disable-next-line
  }, [seasonId]);

  return (
        <div className="app-container">
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <h1>Posázavská liga</h1>
                                        <img src={logo} alt="Logo Posázavské ligy" style={{ height: '240px', marginTop: '10px' }} />
      </div>
      {/* Responzivní SeasonSelector pro veřejné stránky */}
      {location.pathname !== '/admin' && <ResponsiveSeasonSelector seasonId={seasonId} setSeasonId={setSeasonId} admin={isAdmin} />}
      {/* V adminu ho zobrazit na původním místě */}
      {location.pathname === '/admin' && (
          <SeasonSelector seasonId={seasonId} setSeasonId={setSeasonId} admin={isAdmin} />
      )}
      {/* Admin button & logout in top right if logged in */}
      {isAdmin && (
        <div style={{position:'absolute',top:24,right:24,display:'flex',gap:12}}>
          <button onClick={()=>navigate('/admin')} style={{background:'#007bff',color:'#fff',border:'none',borderRadius:4,padding:'6px 16px',fontSize:15,cursor:'pointer'}}>Admin</button>
          <button onClick={handleLogout} style={{background:'#eee',border:'none',borderRadius:4,padding:'6px 16px',fontSize:15,cursor:'pointer'}}>Odhlásit se</button>
        </div>
      )}
      <nav style={{display:'flex', justifyContent:'center', gap:24, marginBottom:32}}>
        <Link to="/" style={{padding:8, fontSize:16, background: active==='standings' ? '#007bff' : '#e9ecef', color: active==='standings' ? '#fff':'#222', border:'none', borderRadius:6, textDecoration:'none'}}>Tabulka</Link>
        <Link to="/matches" style={{padding:8, fontSize:16, background: active==='matches' ? '#007bff' : '#e9ecef', color: active==='matches' ? '#fff':'#222', border:'none', borderRadius:6, textDecoration:'none'}}>Zápasy</Link>
        <Link to="/teams" style={{padding:8, fontSize:16, background: active==='teams' ? '#007bff' : '#e9ecef', color: active==='teams' ? '#fff':'#222', border:'none', borderRadius:6, textDecoration:'none'}}>Týmy</Link>
        <Link to="/pravidla" style={{padding:8, fontSize:16, background: active==='pravidla' ? '#007bff' : '#e9ecef', color: active==='pravidla' ? '#fff':'#222', border:'none', borderRadius:6, textDecoration:'none'}}>Pravidla</Link>

      </nav>
      <div style={{background:'#fff', padding:24, borderRadius:8, minHeight:300}}>
        <Routes>
          <Route path="/teams/:teamId" element={<TeamDetail seasonId={seasonId} />} />
          <Route path="/matches" element={<Matches seasonId={seasonId} />} />
          <Route path="/teams" element={<Teams seasonId={seasonId} />} />
          <Route path="/pravidla" element={<Rules />} />
          <Route path="/admin" element={
            isAdmin
              ? <Admin seasonId={seasonId} />
              : <Login onLogin={handleLogin} />
          } />
          <Route path="/" element={<Standings seasonId={seasonId} />} />
        </Routes>
      </div>
    </div>
  );
}

export default App;
